import React, { useState } from 'react';
import DocumentTemplateSelector from './DocumentTemplateSelector';
import ProspectQuotePrint from './ProspectQuotePrint';
import TextTemplatePicker from './TextTemplatePicker';
import { apiFetch } from './utils/api';
import { generateDocumentOutput } from './utils/documentGeneration';
import { getCompanyVariables } from './utils/templateVariables';

function ProspectQuoteBuilder({
  prospect,
  quoteItems,
  products,
  labour,
  consumables,
  searchTerm,
  setSearchTerm,
  searchCategory,
  setSearchCategory,
  handleAddItem,
  handleUpdateItem,
  handleRemoveItem,
  currentQuote,
  setCurrentQuote,
  onSave,
  onCancel,
  saving
}) {

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showDocumentTemplateSelector, setShowDocumentTemplateSelector] = useState(false);
  const [generatingLineItem, setGeneratingLineItem] = useState(null);

  const addBlankLine = () => {
    const newItem = {
      type: 'manual',
      item_id: null,
      description: '',
      category: 'parts',
      quantity: 1,
      unit: 'each',
      cost_price: 0,
      sell_price: 0
    };
    handleAddItem(newItem);
  };

  const calculateLineTotals = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.cost_price || item.unit_price) || 0;
    const sell = parseFloat(item.sell_price || item.unit_price) || 0;
    const lineCost = qty * cost;
    const lineTotal = qty * sell;
    const lineProfit = lineTotal - lineCost;
    const lineMargin = lineTotal > 0 ? (lineProfit / lineTotal * 100) : 0;
    return { lineCost, lineTotal, lineProfit, lineMargin };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalCost = 0;

    quoteItems.forEach(item => {
      const { lineTotal, lineCost } = calculateLineTotals(item);
      subtotal += lineTotal;
      totalCost += lineCost;
    });

    const vatRate = 20.00;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;
    const totalProfit = subtotal - totalCost;
    const profitMargin = subtotal > 0 ? (totalProfit / subtotal * 100) : 0;

    return { subtotal, vatAmount, total, totalCost, totalProfit, profitMargin };
  };

  const totals = calculateTotals();

  const prepareProspectQuoteData = async () => {
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    return {
      prospect_name: prospect.prospect_name || '',
      customer_name: prospect.prospect_name || '',
      contact_name: prospect.contact_name || '',
      customer_email: prospect.email || '',
      customer_phone: prospect.phone || '',
      customer_address: prospect.address || '',
      quote_number: currentQuote.quote_ref || currentQuote.quote_number || currentQuote.id || '',
      quote_date: currentQuote.quote_date ? new Date(currentQuote.quote_date).toLocaleDateString() : '',
      valid_until: currentQuote.valid_until ? new Date(currentQuote.valid_until).toLocaleDateString() : '',
      work_location: currentQuote.work_location || '',
      site_address: currentQuote.work_location || '',
      door_location: currentQuote.door_location || '',
      description: currentQuote.description || '',
      status: currentQuote.status || 'Draft',
      items: quoteItems.map((item) => {
        const { lineTotal } = calculateLineTotals(item);
        return {
          description: item.description || '',
          category: item.category || '',
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || '',
          unit_price: `£${parseFloat(item.sell_price || item.unit_price || 0).toFixed(2)}`,
          total: `£${lineTotal.toFixed(2)}`,
        };
      }),
      subtotal: `£${totals.subtotal.toFixed(2)}`,
      vat_rate: '20',
      vat_amount: `£${totals.vatAmount.toFixed(2)}`,
      total: `£${totals.total.toFixed(2)}`,
      terms: currentQuote.terms || '',
      notes: currentQuote.notes || '',
      ...getCompanyVariables(companySettings)
    };
  };

  const handleDocumentTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      const templateData = await prepareProspectQuoteData();
      await generateDocumentOutput(template, action, templateData, `ProspectQuote-${currentQuote.quote_ref || currentQuote.id || prospect.prospect_name}`, outputWindow);
      setShowDocumentTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  if (showPrintPreview) {
    return (
      <ProspectQuotePrint
        prospect={prospect}
        currentQuote={currentQuote}
        quoteItems={quoteItems}
        totals={totals}
        onClose={() => setShowPrintPreview(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-sm">
        <div className="max-w-full px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onCancel}
              className="text-white hover:text-teal-100"
            >
              ← Back
            </button>
            <h1 className="text-xl font-semibold">
              New Quote for {prospect.prospect_name}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPrintPreview(true)}
              disabled={quoteItems.length === 0}
              className="px-4 py-1.5 bg-teal-500 text-white rounded text-sm font-medium hover:bg-teal-400 transition disabled:opacity-50"
            >
              👁️ Preview
            </button>
            <button
              onClick={() => setShowDocumentTemplateSelector(true)}
              disabled={quoteItems.length === 0}
              className="px-4 py-1.5 bg-teal-500 text-white rounded text-sm font-medium hover:bg-teal-400 transition disabled:opacity-50"
            >
              Print / PDF
            </button>
            <button
              onClick={() => onSave(totals.total)}
              disabled={saving || quoteItems.length === 0}
              className="px-4 py-1.5 bg-white text-teal-700 rounded text-sm font-medium hover:bg-teal-50 transition disabled:opacity-50"
            >
              {saving ? 'Saving...' : '💾 Save Quote'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-4">

            {/* Section 1: Quote Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-teal-100 text-teal-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                Quote Details
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quote Date</label>
                  <input
                    type="date"
                    value={currentQuote.quote_date || ''}
                    onChange={(e) => setCurrentQuote({...currentQuote, quote_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <input
                    type="date"
                    value={currentQuote.valid_until || ''}
                    onChange={(e) => setCurrentQuote({...currentQuote, valid_until: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Work Location (Site Address)</label>
                  <input
                    type="text"
                    value={currentQuote.work_location || ''}
                    onChange={(e) => setCurrentQuote({...currentQuote, work_location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                    placeholder="Full address where works will be carried out"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Door Location</label>
                  <input
                    type="text"
                    value={currentQuote.door_location || ''}
                    onChange={(e) => setCurrentQuote({...currentQuote, door_location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                    placeholder="e.g., Main entrance, Loading bay door 3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={currentQuote.status || 'Draft'}
                    onChange={(e) => setCurrentQuote({...currentQuote, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                  >
                    <option>Draft</option>
                    <option>Sent</option>
                    <option>Accepted</option>
                    <option>Declined</option>
                  </select>
                </div>
              </div>

              {/* Description of Works */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Description of Works (Optional)</label>
                  <TextTemplatePicker
                    category="Quotes"
                    onInsert={(text) => {
                      const current = currentQuote.description || '';
                      const newText = current ? current + '\n\n' + text : text;
                      setCurrentQuote({...currentQuote, description: newText});
                    }}
                  />
                </div>
                <textarea
                  value={currentQuote.description || ''}
                  onChange={(e) => setCurrentQuote({...currentQuote, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                  placeholder="Brief description of the works to be undertaken..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This text will appear at the start of the quote
                </p>
              </div>
            </div>

            {/* Section 2: Line Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="bg-teal-100 text-teal-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Line Items
                </h2>
                <button
                  onClick={addBlankLine}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded text-sm font-medium hover:bg-teal-700"
                >
                  ➕ Add Line
                </button>
              </div>

              {quoteItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No line items yet. Click "Add Line" to start building the quote.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Description</th>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Category</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600">Qty</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Cost</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Sell</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Total</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Profit</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {quoteItems.map((item, index) => {
                        const { lineTotal, lineProfit, lineMargin } = calculateLineTotals(item);
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 py-2">
                              <input
                                type="text"
                                value={item.description || ''}
                                onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                                placeholder="Item description..."
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <select
                                value={item.category || 'parts'}
                                onChange={(e) => handleUpdateItem(index, 'category', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="parts">Parts</option>
                                <option value="labour">Labour</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.quantity || 0}
                                onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.cost_price || 0}
                                onChange={(e) => handleUpdateItem(index, 'cost_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.sell_price || 0}
                                onChange={(e) => handleUpdateItem(index, 'sell_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2 text-right font-medium">
                              £{lineTotal.toFixed(2)}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <span className={lineProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                £{lineProfit.toFixed(2)}
                              </span>
                              <div className="text-xs text-gray-500">
                                {lineMargin.toFixed(1)}%
                              </div>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => handleRemoveItem(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 3: Terms & Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-teal-100 text-teal-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                Terms & Conditions
              </h2>

              <textarea
                value={currentQuote.terms || ''}
                onChange={(e) => setCurrentQuote({...currentQuote, terms: e.target.value})}
                rows="5"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-teal-500"
                placeholder="Payment terms, warranty information, etc..."
              />
            </div>
          </div>

          {/* Sidebar - 1/3 width - Sticky Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">

              {/* Prospect Info */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Prospect</h3>
                <div className="text-sm space-y-1">
                  <div className="font-medium">{prospect.prospect_name}</div>
                  {prospect.contact_name && (
                    <div className="text-gray-600">{prospect.contact_name}</div>
                  )}
                  {prospect.email && (
                    <div className="text-gray-600">{prospect.email}</div>
                  )}
                  {prospect.phone && (
                    <div className="text-gray-600">{prospect.phone}</div>
                  )}
                  {prospect.address && (
                    <div className="text-gray-600 mt-2">{prospect.address}</div>
                  )}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg shadow p-4 border border-teal-200">
                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT (20%)</span>
                    <span className="font-medium">£{totals.vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-teal-300">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-lg text-teal-700">£{totals.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-teal-300 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-medium text-red-600">£{totals.totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profit</span>
                    <span className={`font-bold ${totals.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      £{totals.totalProfit.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Margin</span>
                    <span className={`font-bold ${totals.profitMargin >= 30 ? 'text-green-600' : totals.profitMargin >= 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {totals.profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {totals.profitMargin < 20 && totals.subtotal > 0 && (
                  <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                    ⚠️ Margin below 20% - review pricing
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DocumentTemplateSelector
        isOpen={showDocumentTemplateSelector}
        onClose={() => setShowDocumentTemplateSelector(false)}
        documentType="prospect_quote"
        placementKey="prospect_quote.print_pdf"
        data={currentQuote}
        onGenerate={handleDocumentTemplateGenerate}
      />
    </div>
  );
}

export default ProspectQuoteBuilder;
